import { action, computed, makeObservable, observable } from 'mobx';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import type {
    CadFeatureMap,
    ConversationMessageView,
    ConversationParticipantView,
    ConversationView,
} from '@mark43/cad-api';
import type {
    ConversationsFilter,
    InsertMessageNudge,
    ActiveFilterTabMenu,
    ConversationItemData,
} from '../types';
import { ConversationKey } from '../models';
import { getElementScrollInfo } from '../helpers/element-helpers';
import cloneDeep from 'lodash/cloneDeep';
import type {
    ConversationMetadata,
    MessagingStoreData,
    ParticipantFiltersInfo,
    ParticipantIdentities,
    ParticipantUnreadInfo,
} from './types';
import {
    getInitialMessagingStoreData,
    buildParticipantKey,
    incrementAggregateUnreadCountsForNudge,
    isActiveParticipant,
    sortIdentitiesByType,
    updateMessagingStoreBookmarkedConversationsData,
    updateMessagingStoreConversationsData,
} from './helpers';
import { getUnreadUrgenciesForConversations, playNewMessageAlert } from '../helpers/sound-helpers';
import { ListStore } from '@list-store';
import { AlertContext, audibleAlerts } from '../../sounds/audible-alerts';
import {
    getBookmarkedConversationListKeys,
    getConversationListKeys,
    getConversations,
    isConversationActive,
} from '../helpers/conversation-helpers';
import {
    getConversationsFilterList,
    getFilterParticipants,
} from '../helpers/conversations-filter-helpers';

type FetchState = {
    isError: boolean;
    isFetching: boolean;
    refetch: () => void;
};

export class MessagingStore {
    @observable
    data: MessagingStoreData;
    @observable
    messagingBootstrapState?: FetchState;
    @observable
    conversationMessagesFetchState?: FetchState;
    @observable
    lastReceivedMessageNudgeId?: number;

    // maintain the last message id of the conversation that has been read from other windows.
    // if message store in other windows mark a conversation as read,
    // message store in this window will keep a {coversationId: newestMessageId} map to track last message ids that has been read from other windows,
    // if the message nudge received in a later time in this window AND the conversation has been read by other windows,
    // then don't consider the new message as unread.
    // NOTE: this map stores the last read message by any other windows, current window store may or may not have received the message.
    globalLastReadMessageIdByConversationId: Record<number, number | undefined>;

    enabledFeatures: CadFeatureMap;
    isCad: boolean;

    constructor(enabledFeatures: CadFeatureMap, isCad: boolean) {
        this.data = getInitialMessagingStoreData();
        this.enabledFeatures = enabledFeatures;
        this.isCad = isCad;
        this.messagingBootstrapState = undefined;
        this.conversationMessagesFetchState = undefined;
        this.globalLastReadMessageIdByConversationId = {};
        makeObservable(this);
    }

    private get alertContext(): AlertContext {
        return { enabledFeatures: this.enabledFeatures, isCad: this.isCad };
    }

    @computed
    get activeConversationsUnreadInfo(): ParticipantUnreadInfo | undefined {
        return this.data.activeConversationsUnreadInfo;
    }

    @computed
    get activeFilterTabMenu(): ActiveFilterTabMenu | undefined {
        return this.data.activeFilterTabMenu;
    }

    @computed
    get bookmarkedConversations(): Record<number, ConversationView> | undefined {
        return this.data.bookmarkedConversations;
    }

    @computed
    get conversations(): Record<number, ConversationView> | undefined {
        return this.data.conversations;
    }

    @computed
    get dispatchAreasUnreadInfo(): ParticipantUnreadInfo | undefined {
        return this.data.dispatchAreasUnreadInfo;
    }

    @computed
    get participantFiltersInfo(): ParticipantFiltersInfo | undefined {
        return this.data.participantFiltersInfo;
    }

    @computed
    get participantIdentities(): ParticipantIdentities | undefined {
        return this.data.participantIdentities;
    }

    @computed
    get conversationItems(): ConversationItemData[] {
        return getConversations(this.conversationListKeys, this.conversations);
    }

    @computed
    get bookmarkedConversationItems(): ConversationItemData[] {
        return getConversations(this.bookmarkedConversationListKeys, this.bookmarkedConversations);
    }

    @computed
    get conversationsMetadata(): Record<string, ConversationMetadata> | undefined {
        return this.data.conversationsMetadata;
    }

    @computed
    get conversationsToBeFetched(): Set<number> {
        return this.data.conversationsToBeFetched;
    }

    @computed
    get selectedConversationKey(): ConversationKey | undefined {
        return this.data.selectedConversationKey;
    }

    @computed
    get isBookmarksView(): boolean {
        return this.data.isBookmarksView;
    }

    @computed
    get selectedConversation() {
        return this.data.selectedConversationKey === undefined
            ? undefined
            : this.data.isBookmarksView
            ? this.data.bookmarkedConversations?.[this.data.selectedConversationKey.conversationId]
            : this.data.conversations?.[this.data.selectedConversationKey.conversationId];
    }

    @computed
    get selectedConversationMessageList() {
        return this.data.selectedConversationMessageList;
    }

    @computed
    get areConversationsFetched() {
        return this.data.conversations !== undefined;
    }

    @computed
    get areBookmarkedConversationsFetched() {
        return this.data.bookmarkedConversations !== undefined;
    }

    @computed
    get conversationMetadata() {
        return this.data.selectedConversationKey !== undefined
            ? this.data.conversationsMetadata[this.data.selectedConversationKey.toString()]
            : undefined;
    }

    @computed
    get isSelectedConversationActive() {
        return (
            this.data.selectedConversationKey !== undefined &&
            this.data.participantIdentities !== undefined &&
            isConversationActive(this.data.selectedConversationKey, this.data.participantIdentities)
        );
    }

    @computed
    get isCreatingNewConversation() {
        return !!this.data.selectedConversationKey?.isNewConversationKey();
    }

    @computed
    get selectedConversationFilter() {
        return this.data.selectedConversationFilter;
    }

    @computed
    get selectedFilterParticipants() {
        // contains all the user's participant identities associated to the currently selected conversation filter
        return getFilterParticipants(
            this.data.selectedConversationFilter,
            this.data.participantIdentities
        );
    }

    @computed
    get conversationListKeys() {
        // conversationListKeys contains the a list of conversationKeys for all the conversations in the sidepanel for
        // the currectly selected filter, which is used by the hotkey next/previous conversation to determine which conversation
        // to select next
        return getConversationListKeys(
            this.selectedFilterParticipants,
            this.data.participantFiltersInfo,
            this.data.conversations
        );
    }

    @computed
    get bookmarkedConversationListKeys() {
        // bookmarkedConversationListKeys contains the a list of conversationKeys for all the conversations in the sidepanel in the bookmarks view
        return getBookmarkedConversationListKeys(this.data.bookmarkedConversations);
    }

    @computed
    get conversationsFilterList() {
        // conversationsFilterList contains all the filters from the filter bar in a flattened list so that the
        // hotkey next/previous filter can determine which filter to select next
        return getConversationsFilterList(this.data.participantIdentities);
    }

    @computed
    get isAddUrgentMessageEnabled() {
        return this.data.isAddUrgentMessageEnabled;
    }

    @computed
    get selectedConversationMessageListLength() {
        return this.data.selectedConversationMessageList?.data.list.length;
    }

    @computed
    get selectedConversationMessages() {
        return this.selectedConversationMessageList?.data.list;
    }

    @action
    setLastReceivedMessageNudgeId = (nudgeId: number) => {
        this.lastReceivedMessageNudgeId = nudgeId;
    };

    @action
    setMessagingBootstrapState = (state: FetchState) => {
        this.messagingBootstrapState = state;
    };

    @action
    setConversationMessagesFetchState = (state: FetchState) => {
        this.conversationMessagesFetchState = state;
    };

    @action
    getMessageIndexById = (id: number) => {
        return this.selectedConversationMessageList?.getItemIndex(id);
    };

    @action
    toggleAddUrgentMessage = () => {
        this.setIsAddUrgentMessageEnabled?.(!this.data.isAddUrgentMessageEnabled);
    };

    @action
    setActiveFilterTabMenu = (activeFilterTabMenu?: ActiveFilterTabMenu) => {
        this.data.activeFilterTabMenu = activeFilterTabMenu;
    };

    @action
    setIsAddUrgentMessageEnabled = (isAddUrgentMessageEnabled: boolean) => {
        this.data.isAddUrgentMessageEnabled = isAddUrgentMessageEnabled;
    };

    @action
    setSelectedConversationFilter = (selectedConversationFilter: ConversationsFilter) => {
        this.data.selectedConversationFilter = selectedConversationFilter;
    };

    @action
    setSelectedConversationMessageList = (
        selectedConversationMessageList?: ListStore<ConversationMessageView>
    ): void => {
        this.data.selectedConversationMessageList = selectedConversationMessageList;
    };

    @action
    setIsBookmarksView = (isBookmarksView: boolean): void => {
        this.data.isBookmarksView = isBookmarksView;
    };

    @action
    setSelectedConversationKey = (selectedConversationKey?: ConversationKey): void => {
        this.data.selectedConversationKey = selectedConversationKey;
    };

    @action
    setBookmarkedConversation = (bookmarkedConversation: ConversationView): void => {
        if (this.data?.bookmarkedConversations) {
            this.data.bookmarkedConversations[bookmarkedConversation.id] = bookmarkedConversation;
        }
    };

    @action
    removeBookmarkedConversation = (conversationId: number): void => {
        delete this.data?.bookmarkedConversations?.[conversationId];
    };

    @action
    setConversationScrollToIndex = (
        conversationKey: ConversationKey,
        scrollToMessageIndex: number
    ) => {
        const conversationKeyString = conversationKey.toString();

        this.data.conversationsMetadata[conversationKeyString] = {
            ...this.data.conversationsMetadata[conversationKeyString],
            scrollToMessageIndex,
            conversationKeyString,
        };
    };

    @action
    setLastReceivedMessageId = (
        conversationKey: ConversationKey,
        lastReceivedMessageId: number
    ) => {
        const conversationKeyString = conversationKey.toString();

        this.data.conversationsMetadata[conversationKeyString] = {
            ...this.data.conversationsMetadata[conversationKeyString],
            lastReceivedMessageId,
            conversationKeyString: conversationKey?.toString(),
        };
    };

    @action
    setNewestMessageForConversation = (message: ConversationMessageView) => {
        const conversation = this.data.conversations?.[message.conversationId];
        if (conversation) {
            conversation.newestMessageDateUtc = message.createdDateUtc;
            conversation.newestMessageId = message.id;
        }
    };

    @action
    clearNewestMessageForConversation = (conversationId: number) => {
        const conversation = this.data.conversations?.[conversationId];
        if (conversation) {
            conversation.newestMessageId = undefined;
        }
    };

    @action
    addConversationToBeFetched = (conversationId: number) => {
        const conversation = this.data.conversations?.[conversationId];
        if (conversation === undefined) {
            this.data.conversationsToBeFetched.add(conversationId);
        }
    };

    @action
    addHistoricalConversationIdsToBeFetched = (historicalConversationIds: number[]) => {
        historicalConversationIds.forEach((conversationId) => {
            if (this.data.conversations?.[conversationId] === undefined) {
                this.data.conversationsToBeFetched.add(conversationId);
            }
        });
    };

    @action
    setActiveParticipantIdentities = (
        activeParticipantIdentities: ConversationParticipantView[]
    ): void => {
        const activeMappedByKey = keyBy(activeParticipantIdentities, (participant) =>
            buildParticipantKey(participant.id, participant.entityType)
        );
        const identitiesByType = groupBy(activeParticipantIdentities, 'entityType');

        const activeMappedByType = {
            USER: identitiesByType.USER[0],
            UNIT: identitiesByType.UNIT?.[0],
            CALL_TAKER_STATION: identitiesByType.CALL_TAKER_STATION?.[0],
            DISPATCH_AREA: identitiesByType.DISPATCH_AREA
                ? sortIdentitiesByType(identitiesByType.DISPATCH_AREA)
                : [],
            DISPATCH_AREA_RESPONDERS: identitiesByType.DISPATCH_AREA_RESPONDERS?.[0],
        };

        this.data.participantIdentities = {
            active: sortIdentitiesByType(activeParticipantIdentities),
            activeMappedByKey,
            activeMappedByType,
            inactive: [],
        };
    };

    @action
    setConversations = (conversations: ConversationView[]): void => {
        const previousConversations = this.data.conversations;
        updateMessagingStoreConversationsData({
            conversations,
            keepExistingStoreData: false,
            messagingStoreData: this.data,
        });
        playNewMessageAlert(
            getUnreadUrgenciesForConversations({
                previousConversations,
                currentConversations: this.data.conversations,
                participantIdentities: this.data.participantIdentities,
            }),
            this.alertContext
        );
    };

    @action
    setBookmarkedConversations = (bookmarkedConversations: ConversationView[]): void => {
        updateMessagingStoreBookmarkedConversationsData({
            bookmarkedConversations,
            storeData: this.data,
        });
    };

    @action
    addConversation = (conversation: ConversationView): void => {
        this.data.conversationsToBeFetched.delete(conversation.id);
        if (this.data.conversations?.[conversation.id] === undefined) {
            updateMessagingStoreConversationsData({
                conversations: [conversation],
                keepExistingStoreData: true,
                messagingStoreData: this.data,
            });
        }
    };

    updateGlobalLastReadMessageIdForConversation = (
        conversationId: number,
        lastReadMessageId: number | undefined
    ): void => {
        this.globalLastReadMessageIdByConversationId[conversationId] = lastReadMessageId;
    };

    @action
    processInsertMessageNudge = (nudge: InsertMessageNudge): void => {
        const {
            id: conversationId,
            createdDateUtc,
            isUrgent: isUrgentMessage,
            messageId,
            senderUserId,
        } = nudge;
        const conversation = this.data?.conversations?.[conversationId];

        if (conversation === undefined) {
            // do nothing if this message is for a conversation that the client is not participating in
            return;
        }

        if (
            //determine if the message is newer than the previous newest message that the client has seen.
            conversation.newestMessageId === undefined ||
            messageId > conversation.newestMessageId
        ) {
            const lastReadMessageIdByOtherWindows = this.globalLastReadMessageIdByConversationId[
                conversationId
            ];

            const isMessageReadByOtherWindows =
                lastReadMessageIdByOtherWindows && lastReadMessageIdByOtherWindows >= messageId;

            conversation.newestMessageId = messageId;
            conversation.newestMessageDateUtc = createdDateUtc;
            if (
                !isMessageReadByOtherWindows &&
                senderUserId !== this.data.participantIdentities?.activeMappedByType.USER.id
            ) {
                let hasActiveParticipant = false;
                // message was sent by someone other than the user so we need to increment the aggregate unread counts
                const updatedParticipantUnreadInfos = conversation.assignedParticipantUnreadInfos.map(
                    (participantUnreadInfo) => {
                        const updatedParticipantUnreadInfo = cloneDeep(participantUnreadInfo);
                        const { participantId, entityType } = updatedParticipantUnreadInfo;
                        // we don't track unread counts for inactive conversations
                        if (
                            isActiveParticipant(
                                buildParticipantKey(participantId, entityType),
                                this.data.participantIdentities
                            )
                        ) {
                            incrementAggregateUnreadCountsForNudge(
                                updatedParticipantUnreadInfo,
                                isUrgentMessage,
                                messageId,
                                this.data
                            );
                            hasActiveParticipant = true;
                        }
                        return updatedParticipantUnreadInfo;
                    }
                );
                // assign new unreadInfos instead of mutating it so useMemo fires
                conversation.assignedParticipantUnreadInfos = updatedParticipantUnreadInfos;
                if (hasActiveParticipant) {
                    audibleAlerts.playAlert({
                        type: 'MESSAGE_RECEIVED',
                        context: this.alertContext,
                        isUrgent: isUrgentMessage,
                    });
                }
            }
        }
    };

    @action
    markConversationAsRead = (conversationId: number): void => {
        const conversation = this.data.conversations?.[conversationId];
        if (conversation && this.data.conversations) {
            const updatedParticipantUnreadInfos = conversation.assignedParticipantUnreadInfos.map(
                (participantUnreadInfo) => ({
                    ...cloneDeep(participantUnreadInfo),
                    oldestUnreadMessageId: undefined,
                    unreadCount: 0,
                    hasStandardUnread: false,
                    hasUrgentUnread: false,
                })
            );
            // assign new unreadInfos instead of mutating so useMemo fires
            conversation.assignedParticipantUnreadInfos = updatedParticipantUnreadInfos;

            // recalculate unreads
            updateMessagingStoreConversationsData({
                conversations: Object.values(this.data.conversations),
                keepExistingStoreData: false,
                messagingStoreData: this.data,
            });
        }
    };

    @action
    trackConversationScroll = (
        conversationKey: ConversationKey,
        messagesScrollElement: HTMLElement
    ): void => {
        if (!messagesScrollElement || !conversationKey) {
            return;
        }
        const conversationKeyString = conversationKey.toString();
        const scrollInfo = getElementScrollInfo(messagesScrollElement);
        const { isScrolledToBottom } = scrollInfo;

        this.data.conversationsMetadata[conversationKeyString] = {
            ...this.data.conversationsMetadata[conversationKeyString],
            isScrolledToBottom,
            conversationKeyString,
        };
    };
}
